import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Pagination, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import Swal from 'sweetalert2';
import Select from 'react-select';

const useSalaryData = (currentPage, perPage, filters) => {
    const [salaryExpenses, setSalaryExpenses] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, lastPage: 1 });
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem("authToken");
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const fetchExpenses = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${process.env.REACT_APP_API_URL}/salary-expenses?page=${currentPage}&per_page=${perPage}`;
            Object.entries(filters).forEach(([key, value]) => {
                if (value) url += `&${key}=${encodeURIComponent(value)}`;
            });
            const res = await axios.get(url, { headers });
            if (res.data.salaryExpenses) {
                setSalaryExpenses(res.data.salaryExpenses.data || []);
                setPagination({
                    total: res.data.salaryExpenses.total || 0,
                    lastPage: res.data.salaryExpenses.last_page || 1
                });
            }
            if (res.data.paymentMethods) setPaymentMethods(res.data.paymentMethods);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, perPage, filters, headers]);

    const fetchFormDependencies = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/salary-expenses/create`, { headers });
            if (res.data.employees) setEmployees(res.data.employees);
            if (res.data.paymentMethods) setPaymentMethods(res.data.paymentMethods);
            return res.data;
        } catch (err) {
            console.error(err);
        }
    }, [headers]);

    // Fetch employees separately for filter
    const fetchEmployees = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/salary-expenses/create`, { headers });
            if (res.data.employees) setEmployees(res.data.employees);
        } catch (err) {
            console.error(err);
        }
    }, [headers]);

    useEffect(() => {
        fetchExpenses();
        fetchEmployees(); // Also fetch employees for filter dropdown
    }, [fetchExpenses, fetchEmployees]);

    return { salaryExpenses, employees, paymentMethods, pagination, loading, fetchExpenses, fetchFormDependencies, headers };
};

export default function SalaryExpenseIndex() {
    const { t } = useContext(TranslatorContext);

    const permissions = useMemo(() => JSON.parse(localStorage.getItem("permissions")) || [], []);
    const hasPermission = useCallback((perm) => permissions.includes(perm), [permissions]);

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(10);
    const [alertModal, setAlertModal] = useState(false);
    const [sidePanelOpen, setSidePanelOpen] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const [filters, setFilters] = useState({ employee_id: '', status: '', from_month: '', created_at: '' });
    const [tempFilters, setTempFilters] = useState({ employee_id: '', status: '', from_month: '', created_at: '' });

    const {
        salaryExpenses, employees, paymentMethods, pagination, loading, fetchExpenses, fetchFormDependencies, headers
    } = useSalaryData(currentPage, perPage, filters);

    const initialFormState = useMemo(() => ({
        created_at: new Date().toISOString().split('T')[0],
        employee_id: '',
        amount: '',
        salary_month: '',
        status: 'Full',
        comment: '',
        payments: [{ amount: '', payment_method_id: '' }]
    }), []);

    const [formData, setFormData] = useState(initialFormState);

    const calculatedTotalPayments = useMemo(() => {
        return formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    }, [formData.payments]);

    const applyFilters = () => { setFilters(tempFilters); setCurrentPage(1); };
    const resetFilters = () => {
        const cleanFilter = { employee_id: '', status: '', from_month: '', created_at: '' };
        setTempFilters(cleanFilter);
        setFilters(cleanFilter);
        setCurrentPage(1);
    };

    const handleEmployeeChange = (selected) => {
        const employeeId = selected?.value || '';
        const selectedEmployee = employees.find(emp => emp.id == employeeId);
        const baseSalary = selectedEmployee?.salary || 0;

        setFormData(prev => ({
            ...prev,
            employee_id: employeeId,
            amount: baseSalary,
            payments: [{ amount: baseSalary, payment_method_id: prev.payments[0]?.payment_method_id || '' }]
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePaymentChange = (index, field, value) => {
        const updatedPayments = [...formData.payments];
        updatedPayments[index][field] = value;
        setFormData(prev => ({ ...prev, payments: updatedPayments }));
    };

    const addPaymentRow = () => setFormData(prev => ({ ...prev, payments: [...prev.payments, { amount: '', payment_method_id: '' }] }));
    const removePaymentRow = (index) => {
        if (formData.payments.length === 1) return;
        setFormData(prev => ({ ...prev, payments: prev.payments.filter((_, i) => i !== index) }));
    };

    const handleOpenSidePanel = async (expense = null) => {
        await fetchFormDependencies();

        if (expense) {
            setIsEditing(true);
            setCurrentId(expense.id);
            const isoDate = expense.created_at ? new Date(expense.created_at).toISOString().split('T')[0] : '';
            setFormData({
                created_at: isoDate,
                employee_id: expense.employee_id || '',
                amount: expense.amount || '',
                salary_month: expense.salary_month || '',
                status: expense.status || 'Full',
                comment: expense.comment || '',
                payments: expense.payments?.map(p => ({
                    amount: p.amount,
                    payment_method_id: p.payment_method_id
                })) || [{ amount: '', payment_method_id: '' }]
            });
        } else {
            setIsEditing(false);
            setCurrentId(null);
            setFormData({
                ...initialFormState,
                created_at: new Date().toISOString().split('T')[0]
            });
        }
        setSidePanelOpen(true);
    };

    const handleCloseSidePanel = () => {
        setSidePanelOpen(false);
        setFormData(initialFormState);
        setIsEditing(false);
        setCurrentId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.status !== 'Advance' && Math.abs(calculatedTotalPayments - parseFloat(formData.amount)) > 0.01) {
            Swal.fire({
                icon: 'error',
                title: t('validation error'),
                text: t('Total payments must equal salary amount'),
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        try {
            const endpoint = `${process.env.REACT_APP_API_URL}/salary-expenses${isEditing ? `/${currentId}` : ''}`;
            const method = isEditing ? 'put' : 'post';

            await axios[method](endpoint, formData, { headers: { ...headers, 'Content-Type': 'application/json' } });

            handleCloseSidePanel();
            fetchExpenses();
            Swal.fire({
                icon: 'success',
                title: t('success'),
                text: isEditing ? t('Updated successfully') : t('Created successfully'),
                toast: true, position: 'top-end', showConfirmButton: false, timer: 3000
            });
        } catch (error) {
            const errMsg = error.response?.data?.errors ? Object.values(error.response.data.errors).flat().join('\n') : error.response?.data?.message || t('Failed to save');
            Swal.fire({ icon: 'error', title: t('error'), text: errMsg, toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/salary-expenses/${id}`, { headers });
            setAlertModal(false);
            fetchExpenses();
            Swal.fire({ icon: 'success', title: t('success'), text: t('Deleted successfully'), toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
        } catch (error) {
            Swal.fire({ icon: 'error', title: t('error'), text: error.response?.data?.message || t('Failed to delete'), toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatCurrency = (val) => {
        const num = Number(val || 0);
        return num % 1 === 0
            ? num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
            : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const employeeFilterOptions = useMemo(() => [
        { value: '', label: t('All Employees') },
        ...employees.map(emp => ({ value: emp.id, label: emp.name }))
    ], [employees, t]);

    const employeeFormOptions = useMemo(() => employees.map(emp => ({
        value: emp.id,
        label: `${emp.name}`
    })), [employees]);

    const handlePageChange = (newPage) => { if (newPage >= 1 && newPage <= pagination.lastPage) setCurrentPage(newPage); };
    const paginationItems = useMemo(() => {
        let items = [];
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(pagination.lastPage, currentPage + 2);

        if (endPage - startPage < 4) {
            startPage > 1 ? startPage = Math.max(1, endPage - 4) : endPage = Math.min(pagination.lastPage, startPage + 4);
        }
        if (startPage > 1) {
            items.push(<Pagination.First key="first" onClick={() => handlePageChange(1)} />, <Pagination.Ellipsis key="el-start" />);
        }
        for (let p = startPage; p <= endPage; p++) {
            items.push(<Pagination.Item key={p} active={p === currentPage} onClick={() => handlePageChange(p)}>{p}</Pagination.Item>);
        }
        if (endPage < pagination.lastPage) {
            items.push(<Pagination.Ellipsis key="el-end" />, <Pagination.Last key="last" onClick={() => handlePageChange(pagination.lastPage)} />);
        }
        return items;
    }, [currentPage, pagination.lastPage]);

    if (loading && salaryExpenses.length === 0) {
        return <PageLayout><div className="p-5 text-center"><div className="spinner-border text-primary" role="status"></div><p className="mt-2">{t("loading")}...</p></div></PageLayout>;
    }

    return (
        <PageLayout>
            <Row className="mb-4">
                <Col xl={12}>
                    <div className="card shadow-sm border-0">
                        <div className="card-body p-3 bg-light rounded d-flex align-items-center justify-content-between">
                            <h3 className="mb-0 text-dark fw-bold text-capitalize" style={{ fontSize: '1.5rem' }}>{t('salary expenses')}</h3>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb mb-0 small font-weight-medium">
                                    <li className="breadcrumb-item text-muted">{t('dashboard')}</li>
                                    <li className="breadcrumb-item active text-primary" aria-current="page">{t('salary expenses')}</li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col xl={12}>
                    <div className="card shadow-sm border-0 mb-4">
                        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
                            <h5 className="mb-0 text-secondary font-weight-bold">{t('salary expenses list')}</h5>
                            {hasPermission("Salary Expenses create") && (
                                <ButtonComponent className="btn btn-primary btn-md px-4 shadow-sm" onClick={() => handleOpenSidePanel()}>
                                    <i className="material-icons align-middle me-1">add</i>
                                    <span className="align-middle">{t('Add Salary Expense')}</span>
                                </ButtonComponent>
                            )}
                        </div>

                        <div className="card-body bg-light border-bottom p-3">
                            <Row className="g-3 alignment-items-end">
                                <Col md={4} lg={3}>
                                    <Form.Label className="small fw-bold text-muted mb-1">{t('employee')}</Form.Label>
                                    <Select
                                        options={employeeFilterOptions}
                                        value={employeeFilterOptions.find(o => o.value == tempFilters.employee_id) || employeeFilterOptions[0]}
                                        onChange={(sl) => setTempFilters(p => ({ ...p, employee_id: sl?.value || '' }))}
                                        isSearchable
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Label className="small fw-bold text-muted mb-1">{t('status')}</Form.Label>
                                    <Form.Select className="form-select-sm shadow-none" value={tempFilters.status} onChange={(e) => setTempFilters(p => ({ ...p, status: e.target.value }))}>
                                        <option value="">{t('All')}</option>
                                        <option value="Full">{t('Full')}</option>
                                        <option value="Advance">{t('Advance')}</option>
                                    </Form.Select>
                                </Col>
                                <Col md={3} lg={2}>
                                    <Form.Label className="small fw-bold text-muted mb-1">{t('salary month')}</Form.Label>
                                    <Form.Control size="sm" type="month" className="shadow-none" value={tempFilters.from_month} onChange={(e) => setTempFilters(p => ({ ...p, from_month: e.target.value }))} />
                                </Col>
                                <Col md={3} lg={2}>
                                    <Form.Label className="small fw-bold text-muted mb-1">{t('date')}</Form.Label>
                                    <Form.Control size="sm" type="date" className="shadow-none" value={tempFilters.created_at} onChange={(e) => setTempFilters(p => ({ ...p, created_at: e.target.value }))} />
                                </Col>
                                <Col md={12} lg={3} className="d-flex align-items-end gap-2 mt-md-3 mt-lg-0 justify-content-lg-end">
                                    <ButtonComponent className="btn btn-sm btn-primary px-3" onClick={applyFilters}>
                                        <i className="material-icons size-sm me-1 align-middle">search</i>{t('search')}
                                    </ButtonComponent>
                                    <ButtonComponent className="btn btn-sm btn-light border text-secondary px-3" onClick={resetFilters}>
                                        <i className="material-icons size-sm me-1 align-middle">refresh</i>{t('reset')}
                                    </ButtonComponent>
                                </Col>
                            </Row>
                        </div>

                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0 custom-data-table">
                                <thead className="table-light text-uppercase font-weight-bold small text-muted">
                                    <tr>
                                        <th style={{ width: '60px' }}>#</th>
                                        <th>{t('date')}</th>
                                        <th>{t('employee')}</th>
                                        <th className="text-end">{t('amount')}</th>
                                        <th>{t('salary month')}</th>
                                        <th>{t('status')}</th>
                                        <th>{t('payment method')}</th>
                                        <th>{t('comment')}</th>
                                        <th style={{ width: '120px' }} className="text-center">{t('actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {salaryExpenses.length === 0 ? (
                                        <tr>
                                            <td colSpan="9" className="text-center py-5 text-muted">
                                                <i className="material-icons display-4 text-opacity-25 text-dark mb-2">layers_clear</i>
                                                <p className="mb-0 font-weight-bold">{t('no salary expenses found')}</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        salaryExpenses.map((item, index) => (
                                            <tr key={item.id} className="transition-all">
                                                <td className="text-muted small">{((currentPage - 1) * perPage) + index + 1}</td>
                                                <td>{item.created_at.split("T")[0]}</td>
                                                <td className="fw-bold text-dark">{item.employee?.name || '—'}</td>
                                                <td className="text-end fw-bold text-dark">{formatCurrency(item.amount)}</td>
                                                <td>
                                                    {item.salary_month ? new Date(item.salary_month).toLocaleString('en-US', { month: 'short', year: 'numeric' }) : '—'}
                                                </td>
                                                <td>
                                                    <span className={`badge rounded-pill px-2.5 py-1.5 font-weight-semibold text-uppercase style-badge ${item.status === 'Full' ? 'bg-success-subtle text-success' : 'bg-info-subtle text-info'}`}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="small lh-sm text-secondary">
                                                        {item.payments?.map((p, i) => (
                                                            <div key={i} className="mb-0.5">
                                                                <span className="badge bg-light text-dark border me-1">{p.method?.name || t('unknown')}</span>
                                                                <span className="fw-bold">{formatCurrency(p.amount)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="text-truncate text-muted small" style={{ maxWidth: '160px' }} title={item.comment}>
                                                        {item.comment || '—'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-1.5">
                                                        {hasPermission("Salary Expenses edit") && (
                                                            <button className="btn btn-icon btn-sm btn-light text-primary rounded-circle shadow-none" onClick={() => handleOpenSidePanel(item)} title={t('edit')}>
                                                                <i className="material-icons" style={{ fontSize: '18px' }}>edit</i>
                                                            </button>
                                                        )}
                                                        <button className="btn btn-icon btn-sm btn-light text-danger rounded-circle shadow-none" onClick={() => { setCurrentId(item.id); setAlertModal(true); }} title={t('delete')}>
                                                            <i className="material-icons" style={{ fontSize: '18px' }}>delete</i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {pagination.total > 0 && (
                            <div className="card-footer bg-white border-top py-3 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2">
                                <div className="text-muted small">
                                    {t('showing')} <span className="fw-bold">{((currentPage - 1) * perPage) + 1}</span> - <span className="fw-bold">{Math.min(currentPage * perPage, pagination.total)}</span> {t('of')} <span className="fw-bold">{pagination.total}</span>
                                </div>
                                <Pagination size="sm" className="mb-0 custom-pagination-styles">
                                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                    {paginationItems}
                                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.lastPage} />
                                </Pagination>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            <Modal show={alertModal} onHide={() => setAlertModal(false)} size="sm" centered className="custom-destructive-modal">
                <Modal.Body className="text-center p-4">
                    <div className="avatar-icon-wrapper rounded-circle bg-danger-subtle mx-auto mb-3 d-flex align-items-center justify-content-center" style={{ width: '64px', height: '64px' }}>
                        <i className="material-icons text-danger display-5">warning</i>
                    </div>
                    <h4 className="fw-bold mb-2 text-dark">{t("Are you sure?")}</h4>
                    <p className="text-muted small px-2">{t("Want to delete this salary expense? This operational pipeline action cannot be reverted.")}</p>
                    <div className="d-flex justify-content-center gap-2 mt-4">
                        <ButtonComponent className="btn btn-sm btn-light border px-3" onClick={() => setAlertModal(false)}>{t('cancel')}</ButtonComponent>
                        <ButtonComponent className="btn btn-sm btn-danger px-3 shadow-sm" onClick={() => handleDelete(currentId)}>{t('delete')}</ButtonComponent>
                    </div>
                </Modal.Body>
            </Modal>

            <div className={`offcanvas offcanvas-end shadow-lg border-0 ${sidePanelOpen ? 'show' : ''}`} style={{ visibility: sidePanelOpen ? 'visible' : 'hidden', width: '100%', maxWidth: '640px', zIndex: 1060 }}>
                {sidePanelOpen && <div className="modal-backdrop fade show" style={{ zIndex: -1 }} onClick={handleCloseSidePanel}></div>}

                <div className="d-flex flex-column h-100 bg-white">
                    <div className="offcanvas-header border-bottom bg-light py-3 px-4 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold text-dark">{isEditing ? t('Edit Salary Expense') : t('Add New Salary Expense')}</h5>
                        <button type="button" className="btn-close shadow-none" onClick={handleCloseSidePanel}></button>
                    </div>

                    <div className="offcanvas-body p-4 overflow-y-auto">
                        <Form onSubmit={handleSubmit} id="salary-expense-form">
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Label className="small fw-bold text-secondary mb-1">{t('date')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="date" name="created_at" className="shadow-none" value={formData.created_at} onChange={handleInputChange} required />
                                </Col>
                                <Col md={6}>
                                    <Form.Label className="small fw-bold text-secondary mb-1">{t('employee')} <span className="text-danger">*</span></Form.Label>
                                    <Select
                                        options={employeeFormOptions}
                                        value={employeeFormOptions.find(o => o.value == formData.employee_id) || null}
                                        onChange={handleEmployeeChange}
                                        placeholder={t('Select Employee')}
                                        isSearchable
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                </Col>
                                <Col md={6}>
                                    <Form.Label className="small fw-bold text-secondary mb-1">{t('amount')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="number" step="0.01" name="amount" className="shadow-none" value={formData.amount} onChange={handleInputChange} required />
                                    <Form.Text className="text-muted x-small">{t('Auto-loaded from employee salary profile')}</Form.Text>
                                </Col>
                                <Col md={6}>
                                    <Form.Label className="small fw-bold text-secondary mb-1">{t('salary month')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="month" name="salary_month" className="shadow-none" value={formData.salary_month} onChange={handleInputChange} required />
                                </Col>
                                <Col md={12}>
                                    <Form.Label className="small fw-bold text-secondary mb-1">{t('status')}</Form.Label>
                                    <Form.Select name="status" className="shadow-none" value={formData.status} onChange={handleInputChange}>
                                        <option value="Full">{t('Full')}</option>
                                        <option value="Advance">{t('Advance')}</option>
                                    </Form.Select>
                                    {formData.status === 'Advance' && <Form.Text className="text-info d-block x-small font-weight-medium">{t('Advance status skips dynamic baseline amount matching controls.')}</Form.Text>}
                                </Col>
                                <Col md={12}>
                                    <Form.Label className="small fw-bold text-secondary mb-1">{t('comment')}</Form.Label>
                                    <Form.Control as="textarea" rows={2} name="comment" className="shadow-none" value={formData.comment} onChange={handleInputChange} placeholder={t('Optional description records')} />
                                </Col>

                                <Col md={12} className="mt-4">
                                    <div className="card border bg-light-subtle rounded-3 p-3">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <Form.Label className="fw-bold mb-0 text-dark small text-uppercase tracking-wider">{t('payment details')}</Form.Label>
                                            <ButtonComponent type="button" className="btn btn-sm btn-success px-2.5 d-flex align-items-center gap-1 shadow-sm" onClick={addPaymentRow}>
                                                <i className="material-icons size-sm">add</i><span>{t('Add Payment')}</span>
                                            </ButtonComponent>
                                        </div>

                                        {formData.payments.map((payment, idx) => (
                                            <Row key={idx} className="g-2 mb-2 align-items-center">
                                                <Col md={5}>
                                                    <Form.Control type="number" step="0.01" className="form-control-sm shadow-none" value={payment.amount} onChange={(e) => handlePaymentChange(idx, 'amount', e.target.value)} placeholder={t('Amount')} required />
                                                </Col>
                                                <Col md={5}>
                                                    <Form.Select className="form-select-sm shadow-none" value={payment.payment_method_id} onChange={(e) => handlePaymentChange(idx, 'payment_method_id', e.target.value)} required>
                                                        <option value="">{t('Select Method')}</option>
                                                        {paymentMethods.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                                    </Form.Select>
                                                </Col>
                                                <Col md={2} className="text-end">
                                                    <ButtonComponent type="button" className="btn btn-sm btn-light text-danger p-1 rounded-circle shadow-none" onClick={() => removePaymentRow(idx)} disabled={formData.payments.length === 1}>
                                                        <i className="material-icons" style={{ fontSize: '20px' }}>delete</i>
                                                    </ButtonComponent>
                                                </Col>
                                            </Row>
                                        ))}

                                        <div className="mt-3 pt-2 border-top d-flex justify-content-between align-items-center small text-secondary">
                                            <span>{t('Calculated Summation Total')}:</span>
                                            <span className={`fw-bold fs-6 ${formData.status !== 'Advance' && Math.abs(calculatedTotalPayments - parseFloat(formData.amount || 0)) > 0.01 ? 'text-danger' : 'text-success'}`}>
                                                {formatCurrency(calculatedTotalPayments)}
                                            </span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </div>

                    <div className="offcanvas-footer p-3 border-top bg-light d-flex justify-content-end gap-2">
                        <ButtonComponent type="button" className="btn btn-sm btn-light border text-secondary px-3" onClick={handleCloseSidePanel}>{t('cancel')}</ButtonComponent>
                        <ButtonComponent type="submit" form="salary-expense-form" className="btn btn-sm btn-primary px-4 shadow-sm">{isEditing ? t('update') : t('save')}</ButtonComponent>
                    </div>
                </div>
            </div>
        </PageLayout>
    );
}